import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Filter } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

/*
  Trial funnel (plan, stage 0): for accounts registered in the period, how many
  reached each step from sign-up to the first conversion, how many did it in
  the first 24 hours, and how long each step took. Read-only, platform admins.
*/

function stepLabel(t, key) {
  switch (key) {
    case 'registered':
      return t('Registration');
    case 'email_confirmed':
      return t('Email confirmed');
    case 'card':
      return t('Card added');
    case 'campaign':
      return t('First campaign');
    case 'stream_with_offer':
      return t('Stream with an offer');
    case 'click':
      return t('First click');
    case 'lead':
      return t('First lead (forms only)');
    case 'conversion':
      return t('First conversion');
    default:
      return key || '—';
  }
}

function outcomeLabel(t, key) {
  switch (key) {
    case 'paid':
      return t('Paid');
    case 'in_trial':
      return t('In trial');
    case 'stopped':
      return t('Stopped: no payment');
    case 'granted':
      return t('Granted by operator');
    default:
      return t('No card');
  }
}

function fmtHours(t, h) {
  if (h === null || h === undefined) return '—';
  if (h < 1) return t('{n} min', { n: Math.max(1, Math.round(h * 60)) });
  if (h < 48) return t('{n} h', { n: Math.round(h * 10) / 10 });
  return t('{n} d', { n: Math.round((h / 24) * 10) / 10 });
}

export default function TrialFunnel() {
  const { t } = useT();
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/platform/trial-funnel`, { params: { days } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Failed to load the trial funnel'));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data?.total || 0;
  const outcomes = data?.outcomes || {};

  return (
    <Card data-testid="trial-funnel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {t('Trial: where clients stop')}
            </CardTitle>
            <CardDescription>
              {t('Accounts registered in the period, without team members and operator accounts. For each step: how many accounts reached it, how many did it within 24 hours of sign-up, and the median time from the previous step.')}
            </CardDescription>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44" aria-label={t('Period: 30 days')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('Period: 7 days')}</SelectItem>
              <SelectItem value="30">{t('Period: 30 days')}</SelectItem>
              <SelectItem value="90">{t('Period: 90 days')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && !data ? (
          <p className="text-sm text-muted-foreground">{t('Loading…')}</p>
        ) : !total ? (
          <p className="text-sm text-muted-foreground">{t('No accounts in this period.')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <div className="text-xs text-muted-foreground">{t('First conversion within 24 hours of sign-up')}</div>
                <div className="text-2xl font-semibold" data-testid="trial-funnel-day-one">
                  {t('{n} of {total} accounts', { n: data.first_conversion_day_one, total })}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {['paid', 'in_trial', 'stopped', 'no_card', 'granted'].map((key) => (
                  <Badge key={key} variant="outline">
                    {outcomeLabel(t, key)}: {outcomes[key] || 0}
                  </Badge>
                ))}
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Step')}</TableHead>
                  <TableHead className="text-right">{t('Accounts')}</TableHead>
                  <TableHead className="text-right">{t('Of sign-ups')}</TableHead>
                  <TableHead className="text-right">{t('Within 24 hours')}</TableHead>
                  <TableHead className="text-right">{t('Median time from previous step')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.steps || []).map((s) => (
                  <TableRow key={s.key}>
                    <TableCell className={s.optional ? 'text-muted-foreground' : ''}>{stepLabel(t, s.key)}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.accounts}</TableCell>
                    <TableCell className="text-right tabular-nums">{Math.round((s.share || 0) * 100)}%</TableCell>
                    <TableCell className="text-right tabular-nums">{s.day_one}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtHours(t, s.median_hours_from_previous)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground">
              {t('Card time is estimated from the trial end date. Email confirmation time is recorded from this release on.')}
            </p>

            <div className="space-y-2">
              <div className="text-sm font-medium">{t('Recent accounts')}</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Email')}</TableHead>
                    <TableHead>{t('Registered')}</TableHead>
                    <TableHead>{t('Stopped at')}</TableHead>
                    <TableHead>{t('Result')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.accounts || []).map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">{a.email || '—'}</TableCell>
                      <TableCell className="text-xs">
                        {a.registered_at ? new Date(a.registered_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.stopped_at ? stepLabel(t, a.stopped_at) : t('All steps done')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{outcomeLabel(t, a.outcome)}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
